import React from 'react';
import { CalendarDays } from 'lucide-react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const Modern = ({ moadData, processedCustomTimes, loading, error, title = 'מועד', showTitle = true, days = [], entryIcon = 'Sun', exitIcon = 'Star', dayLabels = [], drashotByDay = {}, showLeyning, showHaftarah, leyningDisplayMode }) => {
    const EntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['Sun'];
    const ExitIcon = WIDGET_ICON_MAP[exitIcon] || WIDGET_ICON_MAP['Star'];
    if (loading) {
        return (
            <div style={styles.stateContainer}>
                <div style={styles.loading}>טוען זמני מועד...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.stateContainer}>
                <div style={styles.error}>{error}</div>
            </div>
        );
    }

    if (!moadData || !moadData.days || moadData.days.length === 0) {
        return (
            <div style={styles.stateContainer}>
                <div style={styles.loading}>אין נתוני מועד</div>
            </div>
        );
    }

    const firstDay = moadData.days[0];
    const lastDay = moadData.days[moadData.days.length - 1];
    const entryTime = firstDay?.entryTime || '--:--';
    const exitTime = lastDay?.exitTime || '--:--';
    const entryLabel = dayLabels[0]?.entry || "זמן א'";
    const exitLabel = dayLabels[moadData.days.length - 1]?.exit || "זמן ב'";
    const EffectiveEntryIcon = WIDGET_ICON_MAP[dayLabels[0]?.entryIcon] || EntryIcon;
    const EffectiveExitIcon  = WIDGET_ICON_MAP[dayLabels[moadData.days.length - 1]?.exitIcon] || ExitIcon;
    const showEntry = dayLabels[0]?.entry !== null;
    const showExit  = dayLabels[moadData.days.length - 1]?.exit !== null;

    // קיבוץ זמנים מותאמים לפי dayIndex
    const timesByDay = {};
    (processedCustomTimes || []).forEach(ct => {
        const idx = ct.dayIndex ?? 0;
        if (!timesByDay[idx]) timesByDay[idx] = [];
        timesByDay[idx].push(ct);
    });

    // מיון כל קבוצה לפי זמן
    const timeToMinutes = (timeStr) => {
        if (!timeStr || timeStr === '--:--') return -1;
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    Object.values(timesByDay).forEach(group => {
        group.sort((a, b) => (a._sortKey ?? timeToMinutes(a.time)) - (b._sortKey ?? timeToMinutes(b.time)));
    });

    return (
        <div
            style={{
                height: '100%',
                width: '700px',
                display: 'flex',
                flexDirection: 'column',
                direction: 'rtl',
                padding: '12px 16px',
                boxSizing: 'border-box',
            }}
            role="region"
            aria-label="זמני מועד"
        >
            {/* Header */}
            {showTitle && (
                <div style={{
                    padding: '16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e5ddd3'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <CalendarDays style={{ width: '24px', height: '24px', color: '#166534' }} />
                        <h2 style={{ fontWeight: 'bold', fontSize: '24px', color: '#1e3a5f', margin: 0 }}>
                            {title}
                        </h2>
                    </div>
                </div>
            )}

            {/* Holiday Title */}
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
                <h3 style={{ fontSize: '48px', fontWeight: '800', color: '#1e3a5f', margin: 0 }}>
                    {title}
                </h3>
            </div>

            {/* Main Times - Entry / Exit */}
            <div style={{ padding: '0 0 12px' }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: (showEntry && showExit) ? '1fr 1fr' : '1fr',
                    gap: '10px',
                    marginBottom: '16px',
                }}>
                    {/* כניסת המועד */}
                    {showEntry && <div
                        style={{
                            padding: '20px 8px',
                            borderRadius: '12px',
                            textAlign: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            border: '2px solid #166534',
                            minHeight: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                        aria-label={`כניסת מועד בשעה ${entryTime}`}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#166534'
                            }}>
                                <EffectiveEntryIcon style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                        </div>
                        <div style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', color: '#64748b' }}>
                            {entryLabel}
                        </div>
                        <div style={{
                            fontSize: '48px',
                            fontWeight: '800',
                            letterSpacing: '0.05em',
                            color: '#1e3a5f',
                            lineHeight: '1'
                        }}>
                            {entryTime}
                        </div>
                    </div>}

                    {/* יציאת המועד */}
                    {showExit && <div
                        style={{
                            padding: '20px 8px',
                            borderRadius: '12px',
                            textAlign: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            border: '2px solid #1e3a5f',
                            minHeight: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                        aria-label={`יציאת מועד בשעה ${exitTime}`}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#1e3a5f'
                            }}>
                                <EffectiveExitIcon style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                        </div>
                        <div style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', color: '#64748b' }}>
                            {exitLabel}
                        </div>
                        <div style={{
                            fontSize: '48px',
                            fontWeight: '800',
                            letterSpacing: '0.05em',
                            color: '#1e3a5f',
                            lineHeight: '1'
                        }}>
                            {exitTime}
                        </div>
                    </div>}
                </div>

                {/* דרשות */}
                {moadData.days.some((_, i) => drashotByDay[i]) && (
                    <div style={{ marginBottom: '16px' }}>
                        {moadData.days.map((day, dayIdx) => {
                            const drasha = drashotByDay[dayIdx];
                            if (!drasha) return null;
                            const dayColor = days[dayIdx]?.color || '#166534';
                            return (
                                <div key={`drasha-${dayIdx}`} style={{
                                    padding: '14px 18px',
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    border: `2px solid ${dayColor}`,
                                    marginBottom: dayIdx < moadData.days.length - 1 ? '10px' : 0,
                                    textAlign: 'center',
                                }}>
                                    {moadData.days.length > 1 && (
                                        <div style={{
                                            fontSize: '15px', fontWeight: 700,
                                            color: dayColor, marginBottom: '6px',
                                        }}>
                                            {day.label}
                                        </div>
                                    )}
                                    <div style={{
                                        fontSize: '18px', fontWeight: 600,
                                        color: '#1e3a5f', whiteSpace: 'pre-wrap', lineHeight: 1.5,
                                    }}>
                                        {drasha}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Leyning */}
                {(showLeyning || showHaftarah) && moadData.days.some(d => d.leyning) && (
                    <div style={{ marginBottom: '16px' }}>
                        {moadData.days.map((day, dayIdx) => {
                            if (!day.leyning) return null;
                            const dayColor = days[dayIdx]?.color || '#166534';
                            return (
                                <div key={`leyning-${dayIdx}`} style={{
                                    padding: '14px 18px',
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    border: `1px solid #e5ddd3`,
                                    marginBottom: dayIdx < moadData.days.length - 1 ? '10px' : 0,
                                }}>
                                    {moadData.days.length > 1 && (
                                        <div style={{
                                            fontSize: '15px', fontWeight: 700,
                                            color: dayColor, marginBottom: '6px',
                                        }}>
                                            {day.label}
                                        </div>
                                    )}
                                    <LeyningDisplay
                                        leyning={day.leyning}
                                        mode={leyningDisplayMode}
                                        showHaftarah={showHaftarah}
                                        showMaftir={leyningDisplayMode === 'full'}
                                        compact={true}
                                        style={{ padding: '8px 0' }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Custom Times — מקובצים לפי יום */}
                {processedCustomTimes && processedCustomTimes.length > 0 && (
                    <div style={{
                        borderRadius: '12px',
                        padding: '16px 8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        border: '1px solid #e5ddd3'
                    }}>
                        {moadData.days.map((day, dayIdx) => {
                            const dayTimes = timesByDay[dayIdx];
                            if (!dayTimes || dayTimes.length === 0) return null;
                            return (
                                <div key={dayIdx} style={{ marginBottom: dayIdx < moadData.days.length - 1 ? '16px' : 0 }}>
                                    {/* כותרת יום */}
                                    {(() => {
                                        const dayColor = days[dayIdx]?.color || '#166534';
                                        return (
                                            <div style={{
                                                fontSize: '18px',
                                                fontWeight: '700',
                                                color: dayColor,
                                                marginBottom: '10px',
                                                padding: '6px 10px',
                                                backgroundColor: `${dayColor}18`,
                                                borderRadius: '8px',
                                                borderRight: `3px solid ${dayColor}`,
                                                letterSpacing: '0.01em',
                                            }}>
                                                {day.label}
                                            </div>
                                        );
                                    })()}
                                    {/* זמנים של היום */}
                                    {dayTimes.map((ct, idx) => (
                                        <div
                                            key={`${dayIdx}-${idx}`}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                paddingTop: idx > 0 ? '8px' : '0',
                                                paddingBottom: '8px',
                                                marginBottom: '8px',
                                                borderBottom: idx < dayTimes.length - 1 ? '1px solid #f0ebe3' : 'none'
                                            }}
                                        >
                                            <span style={{ fontSize: '22px', fontWeight: '700', color: '#1f3a5f' }}>
                                                {ct.label}
                                            </span>
                                            <span style={{
                                                fontSize: '28px',
                                                fontWeight: '700',
                                                padding: '4px 12px',
                                                borderRadius: '8px',
                                                color: '#1e3a5f',
                                                backgroundColor: '#f0fdf4'
                                            }}>
                                                {ct.time}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    stateContainer: {
        width: '100%',
        padding: '20px',
        textAlign: 'center',
        direction: 'rtl'
    },
    loading: { fontSize: '20px', color: '#1e3a5f' },
    error: { fontSize: '18px', color: '#D32F2F', marginBottom: '10px' }
};

export default Modern;
